import { array, boolean, mixed, number, object, string } from 'yup';
import { PremiumType } from '@app/domain/model/eshop/transport/PremiumType';
import { OwnerStepSchema } from '@app/service/eshop/checkout/schemas/OwnerStepSchema';
import {
    PaymentFrequencyStepSchema,
    PaymentMethodStepSchema,
    PaymentMethodTypeStepSchema,
    PaymentOptionsStepSchema,
} from '@app/service/eshop/checkout/schemas/CheckoutStepSchema';

export const PurchaseTransportInsuranceSteps = {
    'step-1-1': object({
        code: string().min(1, 'eshop.transport.validation.invalidCode'),
        manufactureYear: string().min(1, 'eshop.transport.validation.invalidManufactureYear'),
        licencePlate: string().min(1, 'eshop.transport.validation.invalidLicencePlate'),
        modelMTPL: string().min(1, 'profile.validation.fieldRequired'),
        modelCASCO: string().min(1, 'profile.validation.fieldRequired'),
        modificationCodeCasco: string().optional(),
        modificationCodeMtpl: string().optional(),
        deductiblePackageCodeCasco: string().optional(),
        deductiblePackageCodeMtpl: string().optional(),
        alarmLevelCasco: string().optional(),
        alarmLevelMtpl: string().optional(),
    }),
    'step-1-2': object({
        discountCode: string(),
        discountCodeEcaPap: string().optional(),
        discountCodeCasco: string().optional(),
        insuranceDuration: string().min(1, 'eshop.transport.validation.invalidInsuranceDuration'),
        purposeOfTheVehicle: string().min(1, 'eshop.transport.validation.invalidPurposeOfTheVehicle'),
    }),
    'step-1-3': object({
        discountCode: string(),
        drivingExperience: string().when('$isCompany', {
            is: true,
            then: schema => schema.optional(),
            otherwise: schema => schema.min(1, 'eshop.transport.validation.invalidDrivingExperience'),
        }),
        ownerAge: string().min(1, 'eshop.transport.validation.invalidOwnerAge'),
        experienceOtherDrivers: string().min(1, 'eshop.transport.validation.invalidExperienceOtherDrivers'),
        leasing: string(),
        territory: string().min(1, 'eshop.transport.validation.invalidTerritory'),
    }),
    'step-2-1': object({
        insurancePackage: string().min(1, 'eshop.transport.validation.invalidInsurancePackage'),
    }),
    'step-2-2': object({
        cascoPolicyGuid: string().when('insurancePackage', {
            is: (insurancePackage: string) =>
                insurancePackage === PremiumType.Casco || insurancePackage === PremiumType.CascoMtpl,
            then: schema =>
                schema
                    .min(1, 'eshop.transport.validation.invalidCascoPremium')
                    .required('eshop.transport.validation.invalidCascoPremium'),
            otherwise: schema => schema,
        }),
        cascoPolicyPackageCode: string().when('insurancePackage', {
            is: (insurancePackage: string) =>
                insurancePackage === PremiumType.Casco || insurancePackage === PremiumType.CascoMtpl,
            then: schema =>
                schema
                    .min(1, 'eshop.transport.validation.invalidCascoPremium')
                    .required('eshop.transport.validation.invalidCascoPremium'),
            otherwise: schema => schema,
        }),
        mtplPolicyGuid: string().when('insurancePackage', {
            is: (insurancePackage: string) =>
                insurancePackage === PremiumType.Mtpl || insurancePackage === PremiumType.CascoMtpl,
            then: schema =>
                schema
                    .min(1, 'eshop.transport.validation.invalidCascoPremium')
                    .required('eshop.transport.validation.invalidCascoPremium'),
            otherwise: schema => schema,
        }),
        mtplPolicyPackageCode: string().when('insurancePackage', {
            is: (insurancePackage: string) =>
                insurancePackage === PremiumType.Mtpl || insurancePackage === PremiumType.CascoMtpl,
            then: schema =>
                schema
                    .min(1, 'eshop.transport.validation.invalidCascoPremium')
                    .required('eshop.transport.validation.invalidCascoPremium'),
            otherwise: schema => schema,
        }),
        discountDataMtpl: array()
            .when('$self', (value, schema) => {
                if (!value || value.length === 0) {
                    return schema.of(mixed());
                }

                return schema.of(
                    object({
                        type: string().min(1, 'eshop.transport.validation.invalidDiscountType'),
                        code: string().min(1, 'eshop.transport.validation.invalidDiscountCode'),
                        discount: number().required('eshop.transport.validation.invalidDiscountDeductible'),
                    }),
                );
            })
            .optional()
            .default([]),
        discountDataCasco: array()
            .when('$self', (value, schema) => {
                if (!value || value.length === 0) {
                    return schema.of(mixed());
                }

                return schema.of(
                    object({
                        type: string().min(1, 'eshop.transport.validation.invalidDiscountType'),
                        code: string().min(1, 'eshop.transport.validation.invalidDiscountCode'),
                        discount: number().required('eshop.transport.validation.invalidDiscountDeductible'),
                    }),
                );
            })
            .optional()
            .default([]),
    }),
    'step-2-3': object({
        ridersTypeCodes: array()
            .of(
                object({
                    code: string().nullable(),
                    id: string().required('eshop.transport.validation.invalidRiderTypeCode'),
                }),
            )
            .optional()
            .default([]),
    }),
    'step-2-4': object({
        cascoStartDate: string().when('insurancePackage', {
            is: (insurancePackage: string) =>
                insurancePackage === PremiumType.Casco || insurancePackage === PremiumType.CascoMtpl,
            then: schema => schema.min(1, 'profile.validation.fieldRequired'),
            otherwise: schema => schema,
        }),
        cascoEndDate: string().when('insurancePackage', {
            is: (insurancePackage: string) =>
                insurancePackage === PremiumType.Casco || insurancePackage === PremiumType.CascoMtpl,
            then: schema => schema.min(1, 'profile.validation.fieldRequired'),
            otherwise: schema => schema,
        }),
        mtplStartDate: string().when('insurancePackage', {
            is: (insurancePackage: string) =>
                insurancePackage === PremiumType.Mtpl || insurancePackage === PremiumType.CascoMtpl,
            then: schema => schema.min(1, 'profile.validation.fieldRequired'),
            otherwise: schema => schema,
        }),
        mtplEndDate: string().when('insurancePackage', {
            is: (insurancePackage: string) =>
                insurancePackage === PremiumType.Mtpl || insurancePackage === PremiumType.CascoMtpl,
            then: schema => schema.min(1, 'profile.validation.fieldRequired'),
            otherwise: schema => schema,
        }),
    }),
    'step-2-5': object({}),
    'step-2-6': object({}).concat(OwnerStepSchema),
    'step-2-7': object({
        agreeToPrivacyPolicy: boolean().is([true], 'eshop.transport.validation.mustAcceptPrivacyPolicy'),
        agreeToMtplTerms: boolean().when('insurancePackage', {
            is: (insurancePackage: string) =>
                insurancePackage === PremiumType.Mtpl || insurancePackage === PremiumType.CascoMtpl,
            then: schema => schema.is([true], 'eshop.transport.validation.mustAcceptMtplTerms'),
            otherwise: schema => schema,
        }),
        agreeToCascoTerms: boolean().when('insurancePackage', {
            is: (insurancePackage: string) =>
                insurancePackage === PremiumType.Casco || insurancePackage === PremiumType.CascoMtpl,
            then: schema => schema.is([true], 'eshop.transport.validation.mustAcceptCascoTerms'),
            otherwise: schema => schema,
        }),
        agreeToDirectMarketingCompensa: boolean(),
        agreeToDirectMarketingCompensaLife: boolean(),
    })
        .concat(OwnerStepSchema)
        .concat(PaymentFrequencyStepSchema),
    'step-3-1': PaymentOptionsStepSchema,
    'step-3-2': PaymentMethodTypeStepSchema,
    'step-3-3': PaymentMethodStepSchema,
};
